import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowClockwiseIcon, PlugIcon, SpinnerGapIcon, WarningIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { useAvailableProviders, useConnectProvider } from '~/hooks/queries/use-providers';
import { openConnectTab, friendlyConnectError } from '~/lib/nango-connect';
import type { ConnectIntegrationSignalData } from './tool-signals';

export interface ConnectIntegrationCardProps extends ConnectIntegrationSignalData {
  /**
   * Text of the user message this assistant turn is replying to. Resent
   * verbatim as a new user turn when Retry is clicked post-connect.
   */
  originalUserText?: string;
  /**
   * Threaded from the chat hook's sendMessage (same pattern as
   * `QuestionsCard` — see `message-bubble.tsx`). Fires the Retry follow-up
   * turn.
   */
  onSendMessage?: (text: string) => void | Promise<void>;
}

/**
 * FRONT-847/FRONT-985: renders a `connect-integration` signal inline in the
 * assistant message. Connect/Reconnect reuse the same shared connect flow as
 * the Connections page (`openConnectTab` + `useConnectProvider`): the tab is
 * opened synchronously inside the click handler (so the browser doesn't
 * treat it as a blocked popup), then handed to the mutation which navigates
 * it to the provider's OAuth and polls until the connection lands. Once that
 * mutation succeeds the button swaps to a one-tap Retry that re-sends the
 * original user request as a new turn. A connect card auto-hides once
 * `useAvailableProviders` reports the provider connected — whichever surface
 * caused that (this card, the Connections page, a reload) — so it never
 * lingers past the point it's needed. A reconnect card does not: its provider
 * already reads as connected, so it stays until reconnected through it.
 */
export function ConnectIntegrationCard({
  providerKey,
  providerName,
  capability,
  flavour,
  retryable,
  originalUserText,
  onSendMessage,
}: ConnectIntegrationCardProps) {
  const { data: providers = [] } = useAvailableProviders();
  const connect = useConnectProvider();
  // Set when openConnectTab() itself returns null (browser blocked the popup)
  // — that never reaches the mutation, so it can't surface via connect.error.
  const [popupBlocked, setPopupBlocked] = useState(false);

  if (!providerKey) return null;

  const provider = providers.find((p) => p.uniqueKey === providerKey);

  // Auto-hide: server truth wins, but only once we're past the moment this
  // card's own mutation just succeeded. `useConnectProvider`'s poll resolves
  // only after the server confirms the connection, and its `onSuccess`
  // invalidates+refetches the providers list — so the very next render can
  // see `alreadyConnected: true` while `connect.isSuccess` is still true.
  // Hiding on that combination would make the one-tap Retry disappear before
  // the user can tap it. A freshly-loaded already-connected card (page
  // reload, mutation never fired) has `connect.isSuccess === false`, so it
  // still hides as intended.
  // A reconnect card is raised precisely because the stored connection still
  // reads as connected while the provider rejects its token (FRONT-2602).
  if (flavour !== 'reconnect' && provider?.alreadyConnected && !connect.isSuccess) return null;

  if (connect.isSuccess) {
    // FRONT-978: `retryable === false` means this card was raised
    // proactively (no blocked task behind it) — Retry would just resend a
    // "how do I connect X" message pointlessly, so it's omitted regardless
    // of `originalUserText`.
    const canRetry = Boolean(originalUserText) && retryable !== false;
    return (
      <div className="flex items-center gap-3 px-4 py-3 border border-border rounded-lg bg-card max-w-[85%]">
        <PlugIcon size={16} className="shrink-0 text-success" />
        <div className="flex-1 min-w-0">
          <span className="text-xs text-foreground">{providerName} connected</span>
          <Link
            to="/system-settings?section=connections"
            className="block text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Manage connections
          </Link>
        </div>
        {canRetry && (
          <Button
            type="button"
            size="sm"
            onClick={() => onSendMessage?.(originalUserText as string)}
          >
            Retry
          </Button>
        )}
      </div>
    );
  }

  /**
   * FRONT-1205: the org has no OAuth client provisioned for this provider, so
   * a connect attempt cannot succeed — the proxy answers 409 not_configured.
   * The Connections page already refuses this click; this card did not, and
   * opened a tab that died on an error page. Only an admin can fix it, so say
   * that instead of offering a button that cannot work.
   */
  // Only an explicit `false` refuses. A payload that omits the field tells us
  // nothing, and disabling the button on a missing field would take the whole
  // connect path out on an older or partial response.
  const notConfigured = provider?.configured === false;

  const handleConnect = () => {
    if (!provider || notConfigured) return;
    // Open the blank tab synchronously, inside the click handler — this is
    // the whole popup fix (a tab opened after an awaited call loses gesture
    // trust and gets blocked). See `nango-connect.ts`.
    const tab = openConnectTab();
    if (!tab) {
      setPopupBlocked(true);
      return;
    }
    setPopupBlocked(false);
    connect.mutate({ provider, tab });
  };

  const isReconnect = flavour === 'reconnect';
  let errorText: string | null = null;
  if (notConfigured) {
    errorText = friendlyConnectError('not_configured');
  } else if (popupBlocked) {
    errorText = friendlyConnectError('popup_blocked');
  } else if (connect.isError) {
    errorText = friendlyConnectError((connect.error as Error).message);
  }

  return (
    <div className="flex items-center gap-2 px-4 py-3 border border-border rounded-lg bg-card max-w-[85%]">
      <PlugIcon size={16} className="shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-foreground">
          {isReconnect
            ? `Reconnect ${providerName} to continue`
            : `Connect ${providerName} to continue`}
        </span>
        {capability && (
          <div className="text-xs text-muted-foreground mt-0.5">Needed for: {capability}</div>
        )}
        {connect.isPending && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
            <SpinnerGapIcon size={12} className="shrink-0 animate-spin" />
            <span>Finish signing in, in the other tab</span>
          </div>
        )}
        {errorText && (
          <div className="flex items-start gap-1.5 text-xs text-destructive mt-0.5">
            <WarningIcon size={12} weight="fill" className="shrink-0 mt-0.5" />
            <span className="break-words">{errorText}</span>
          </div>
        )}
      </div>
      {connect.isPending ? (
        // Cancel is always live — cancel() aborts the poll and closes the tab.
        <Button type="button" size="sm" variant="outline" onClick={() => connect.cancel()}>
          <XIcon size={12} /> Cancel
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          disabled={!provider || notConfigured}
          onClick={handleConnect}
        >
          {errorText && !notConfigured ? (
            <>
              <ArrowClockwiseIcon size={12} /> Retry
            </>
          ) : isReconnect ? (
            'Reconnect'
          ) : (
            'Connect'
          )}
        </Button>
      )}
    </div>
  );
}
